import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  Building2,
  Plus,
  Search,
  Settings,
  Shield,
  Eye,
  Edit,
  Trash2,
  Key,
  ChevronRight,
  Clock,
  Activity,
  UserPlus,
  X,
  Check,
  AlertTriangle,
  RefreshCw,
  Briefcase,
  Mail,
  Phone,
  MapPin,
  TrendingUp,
  Award,
  Star,
  Target,
  BarChart3,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  Filter,
  Wrench,
  CheckCircle2,
  AlertCircle,
  Home,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function SuperAgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [companyData, setCompanyData] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  
  // New state for dashboard
  const [overview, setOverview] = useState(null);
  const [ticketDistribution, setTicketDistribution] = useState([]);
  const [buildingsTable, setBuildingsTable] = useState([]);
  const [agentsOverview, setAgentsOverview] = useState([]);
  const [agentMapping, setAgentMapping] = useState(null);
  const [ticketTrends, setTicketTrends] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildingAnalytics, setBuildingAnalytics] = useState(null);
  const [drilldownTickets, setDrilldownTickets] = useState(null);
  
  // Sorting and filtering
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [agentFilter, setAgentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showEditStaff, setShowEditStaff] = useState(null);
  const [showAssignBuilding, setShowAssignBuilding] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showTicketDrilldown, setShowTicketDrilldown] = useState(false);
  const [showBuildingDetail, setShowBuildingDetail] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Form state
  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'agent',
    permission_level: 'read_write',
    job_title: '',
    assigned_buildings: [],
  });
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  useEffect(() => {
    if (companyData?.company?.company_id && activeTab === 'overview') {
      fetchDashboardOverview();
    }
  }, [companyData, activeTab]);
  
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const dashRes = await fetch(`${API_URL}/api/agent-company/my-dashboard`, {
        credentials: 'include'
      });
      
      if (dashRes.ok) {
        const data = await dashRes.json();
        setCompanyData(data);
        
        if (data.company?.company_id) {
          // Fetch staff list
          const staffRes = await fetch(`${API_URL}/api/agent-company/staff/${data.company.company_id}`, {
            credentials: 'include'
          });
          if (staffRes.ok) {
            setStaffList(await staffRes.json());
          }
          
          // Fetch buildings
          const buildingsRes = await fetch(`${API_URL}/api/agent-company/buildings/${data.company.company_id}`, {
            credentials: 'include'
          });
          if (buildingsRes.ok) {
            setBuildings(await buildingsRes.json());
          }
          
          // Fetch activity logs
          const logsRes = await fetch(`${API_URL}/api/agent-company/activity-logs/${data.company.company_id}?limit=50`, {
            credentials: 'include'
          });
          if (logsRes.ok) {
            const logsData = await logsRes.json();
            setActivityLogs(logsData.logs || []);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDashboardOverview = async () => {
    if (!companyData?.company?.company_id) return;
    const companyId = companyData.company.company_id;
    
    try {
      // Fetch all dashboard data in parallel
      const [overviewRes, distributionRes, buildingsRes, agentsRes, mappingRes, trendsRes] = await Promise.all([
        fetch(`${API_URL}/api/super-dashboard/overview/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-dashboard/ticket-distribution/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-dashboard/buildings-table/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-dashboard/agents-overview/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-dashboard/agent-building-mapping/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-dashboard/ticket-trends/${companyId}?period=weekly`, { credentials: 'include' })
      ]);
      
      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (distributionRes.ok) {
        const dist = await distributionRes.json();
        setTicketDistribution(dist.distribution || []);
      }
      if (buildingsRes.ok) {
        const bld = await buildingsRes.json();
        setBuildingsTable(bld.buildings || []);
      }
      if (agentsRes.ok) {
        const agt = await agentsRes.json();
        setAgentsOverview(agt.agents || []);
      }
      if (mappingRes.ok) setAgentMapping(await mappingRes.json());
      if (trendsRes.ok) {
        const trends = await trendsRes.json();
        setTicketTrends(trends.trends || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
    }
  };
  
  const fetchBuildingAnalytics = async (hoaId) => {
    if (!companyData?.company?.company_id) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/super-dashboard/building-analytics/${companyData.company.company_id}/${hoaId}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setBuildingAnalytics(await res.json());
        setShowBuildingDetail(true);
      }
    } catch (error) {
      console.error('Error fetching building analytics:', error);
    }
  };
  
  const fetchTicketsByStatus = async (status) => {
    if (!companyData?.company?.company_id) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/super-dashboard/tickets-by-status/${companyData.company.company_id}/${status}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setDrilldownTickets({ status, ...data });
        setShowTicketDrilldown(true);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };
  
  const fetchPerformanceData = async () => {
    if (!companyData?.company?.company_id) return;
    
    setLoadingPerformance(true);
    try {
      const res = await fetch(`${API_URL}/api/agent-company/performance/${companyData.company.company_id}/overview?days=30`, {
        credentials: 'include'
      });
      if (res.ok) {
        setPerformanceData(await res.json());
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoadingPerformance(false);
    }
  };
  
  const handleExport = async (format) => {
    if (!companyData?.company?.company_id) return;
    setExporting(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/super-dashboard/export/${companyData.company.company_id}/${format}`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        const data = await res.json();
        
        if (format === 'excel') {
          // Download CSV
          const blob = new Blob([data.content], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename;
          a.click();
          window.URL.revokeObjectURL(url);
        } else {
          // Download PDF
          const binary = atob(data.content);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        
        toast.success(`Report exported as ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };
  
  const handleCreateStaff = async () => {
    try {
      const res = await fetch(`${API_URL}/api/agent-company/staff/${companyData.company.company_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newStaff),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Staff member created! Temporary password: ${data.temp_password}`);
        setShowAddStaff(false);
        setNewStaff({ name: '', email: '', phone: '', role: 'agent', permission_level: 'read_write', job_title: '', assigned_buildings: [] });
        fetchDashboardData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create staff member');
      }
    } catch (error) {
      toast.error('Failed to create staff member');
    }
  };
  
  const handleUpdateStaff = async () => {
    if (!showEditStaff) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agent-company/staff/${companyData.company.company_id}/${showEditStaff.agent_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: showEditStaff.name,
          phone: showEditStaff.phone,
          permission_level: showEditStaff.permission_level,
          job_title: showEditStaff.job_title,
          status: showEditStaff.status,
        }),
      });
      
      if (res.ok) {
        toast.success('Staff member updated');
        setShowEditStaff(null);
        fetchDashboardData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update staff member');
    }
  };
  
  const handleRemoveStaff = async (agentId) => {
    if (!confirm('Are you sure you want to remove this staff member?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agent-company/staff/${companyData.company.company_id}/${agentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        toast.success('Staff member removed');
        fetchDashboardData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove');
      }
    } catch (error) {
      toast.error('Failed to remove staff member');
    }
  };
  
  const handleResetPassword = async () => {
    if (!showResetPassword) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agent-company/staff/${companyData.company.company_id}/${showResetPassword.agent_id}/reset-password`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Password reset! New temporary password: ${data.temp_password}`);
        setShowResetPassword(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };
  
  const handleAssignBuilding = async (agentId, hoaId, permissionLevel) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-company/buildings/${companyData.company.company_id}/assign-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agent_id: agentId, hoa_id: hoaId, permission_level: permissionLevel }),
      });
      
      if (res.ok) {
        toast.success('Building assigned successfully');
        setShowAssignBuilding(null);
        fetchDashboardData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to assign building');
      }
    } catch (error) {
      toast.error('Failed to assign building');
    }
  };
  
  const handleRevokeBuilding = async (agentId, hoaId) => {
    if (!confirm('Revoke access to this building?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agent-company/buildings/${companyData.company.company_id}/revoke-agent/${agentId}/${hoaId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        toast.success('Access revoked');
        fetchDashboardData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to revoke access');
      }
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const sortedBuildings = [...buildingsTable].sort((a, b) => {
    const aVal = a[sortConfig.key] || '';
    const bVal = b[sortConfig.key] || '';
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    if (typeof aVal === 'number') {
      return (aVal - bVal) * direction;
    }
    return aVal.toString().localeCompare(bVal.toString()) * direction;
  });
  
  const filteredBuildings = sortedBuildings.filter(b => {
    if (agentFilter && !b.assigned_agents?.some(a => a.agent_id === agentFilter)) return false;
    if (statusFilter === 'has_open' && b.open_tickets === 0) return false;
    if (statusFilter === 'no_open' && b.open_tickets > 0) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return b.name?.toLowerCase().includes(q) || b.address?.toLowerCase().includes(q);
    }
    return true;
  });
  
  const filteredStaff = staffList.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="w-4 h-4 text-emerald-500" />;
      case 'update': return <Edit className="w-4 h-4 text-blue-500" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'assign': return <UserPlus className="w-4 h-4 text-purple-500" />;
      case 'revoke': return <X className="w-4 h-4 text-orange-500" />;
      case 'login': return <Key className="w-4 h-4 text-slate-500" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (!companyData?.agent?.is_super_agent && user?.role !== 'platform_owner') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">You do not have Super Agent privileges to access this page.</p>
            <Button className="mt-4" onClick={() => navigate('/agent')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-agent-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/agent')} className="mr-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Company Dashboard</h1>
                <p className="text-sm text-slate-500">{companyData?.company?.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate('/agent/super-reports')}>
                <BarChart3 className="w-4 h-4 mr-2" />
                Reports
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={exporting}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting}>
                <FileText className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <Button variant="outline" onClick={fetchDashboardData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          if (tab === 'performance' && !performanceData) fetchPerformanceData();
        }}>
          <TabsList className="bg-white border border-slate-200 p-1 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="buildings" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Building2 className="w-4 h-4 mr-2" />
              Buildings
            </TabsTrigger>
            <TabsTrigger value="staff" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Users className="w-4 h-4 mr-2" />
              Staff
            </TabsTrigger>
            <TabsTrigger value="assignments" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <MapPin className="w-4 h-4 mr-2" />
              Assignments
            </TabsTrigger>
            <TabsTrigger value="performance" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <TrendingUp className="w-4 h-4 mr-2" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <Building2 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{overview?.summary?.total_buildings || 0}</p>
                  <p className="text-xs text-slate-500">Buildings</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{overview?.summary?.total_agents || 0}</p>
                  <p className="text-xs text-slate-500">Agents</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <Wrench className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{overview?.summary?.total_active_tickets || 0}</p>
                  <p className="text-xs text-slate-500">Active Tickets</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{overview?.summary?.total_open_tickets || 0}</p>
                  <p className="text-xs text-slate-500">Open Tickets</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{overview?.summary?.total_closed_tickets || 0}</p>
                  <p className="text-xs text-slate-500">Closed Tickets</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <Briefcase className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{overview?.summary?.total_contractors_assigned || 0}</p>
                  <p className="text-xs text-slate-500">Contractors</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Ticket Distribution Pie Chart */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-600" />
                    Ticket Distribution
                  </CardTitle>
                  <CardDescription>Click a section to view tickets</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={ticketDistribution}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                          nameKey="status"
                          label={({ status, value }) => `${status}: ${value}`}
                          onClick={(data) => fetchTicketsByStatus(data.raw_status)}
                          style={{ cursor: 'pointer' }}
                        >
                          {ticketDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Ticket Trends Line Chart */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                    Ticket Trends (Weekly)
                  </CardTitle>
                  <CardDescription>Tickets logged vs closed over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={ticketTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="logged" stroke="#6366f1" name="Logged" strokeWidth={2} />
                        <Line type="monotone" dataKey="closed" stroke="#22c55e" name="Closed" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Agents Overview */}
            <Card className="border-slate-200 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Agent Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {agentsOverview.map((agent) => (
                    <div 
                      key={agent.agent_id}
                      className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => setShowEditStaff(staffList.find(s => s.agent_id === agent.agent_id))}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          agent.role === 'super_agent' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}>
                          {agent.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{agent.name}</p>
                          <p className="text-xs text-slate-500">{agent.email}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-lg font-bold text-blue-600">{agent.building_count}</p>
                          <p className="text-xs text-slate-500">Buildings</p>
                        </div>
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-lg font-bold text-amber-600">{agent.open_tickets}</p>
                          <p className="text-xs text-slate-500">Open</p>
                        </div>
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-lg font-bold text-emerald-600">{agent.closed_tickets}</p>
                          <p className="text-xs text-slate-500">Closed</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Buildings Tab */}
          <TabsContent value="buildings">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Building Overview</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search buildings..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <select
                    className="px-3 py-2 border border-slate-200 rounded-md text-sm"
                    value={agentFilter}
                    onChange={(e) => setAgentFilter(e.target.value)}
                  >
                    <option value="">All Agents</option>
                    {staffList.map(s => (
                      <option key={s.agent_id} value={s.agent_id}>{s.name}</option>
                    ))}
                  </select>
                  <select
                    className="px-3 py-2 border border-slate-200 rounded-md text-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="has_open">Has Open Tickets</option>
                    <option value="no_open">No Open Tickets</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600 cursor-pointer hover:bg-slate-50" onClick={() => handleSort('name')}>
                          <div className="flex items-center gap-1">
                            Building Name
                            <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600 cursor-pointer hover:bg-slate-50" onClick={() => handleSort('unit_count')}>
                          <div className="flex items-center gap-1">
                            Units
                            <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Assigned Agent(s)</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600 cursor-pointer hover:bg-slate-50" onClick={() => handleSort('open_tickets')}>
                          <div className="flex items-center gap-1">
                            Open Tickets
                            <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600 cursor-pointer hover:bg-slate-50" onClick={() => handleSort('closed_tickets')}>
                          <div className="flex items-center gap-1">
                            Closed Tickets
                            <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Last Activity</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBuildings.map((building) => (
                        <tr key={building.hoa_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <div>
                                <p className="font-medium text-slate-900">{building.name}</p>
                                <p className="text-xs text-slate-500">{building.address}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">{building.unit_count} units</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {building.assigned_agents?.length > 0 ? (
                                building.assigned_agents.map(a => (
                                  <Badge key={a.agent_id} variant="secondary" className="text-xs">
                                    {a.name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-slate-400 italic">Unassigned</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`font-semibold ${building.open_tickets > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              {building.open_tickets}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-emerald-600">{building.closed_tickets}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs text-slate-500">
                              {building.last_activity ? new Date(building.last_activity).toLocaleDateString() : 'N/A'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => fetchBuildingAnalytics(building.hoa_id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredBuildings.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No buildings found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Staff Tab */}
          <TabsContent value="staff">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Staff Members</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search staff..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Button onClick={() => setShowAddStaff(true)} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Staff
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredStaff.map((staff) => (
                    <div 
                      key={staff.agent_id}
                      className="p-4 bg-slate-50 rounded-xl flex items-center justify-between hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                          staff.role === 'super_agent' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}>
                          {staff.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900">{staff.name}</p>
                            {staff.role === 'super_agent' && (
                              <Badge className="bg-purple-100 text-purple-700">Super Agent</Badge>
                            )}
                            <Badge variant={staff.status === 'active' ? 'default' : 'secondary'} className={
                              staff.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                              staff.status === 'suspended' ? 'bg-amber-100 text-amber-700' : ''
                            }>
                              {staff.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">{staff.email}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {staff.assigned_buildings?.length || 0} buildings
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {staff.permission_level === 'read_write' ? 'Read & Write' : 'Read Only'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setShowAssignBuilding(staff)}>
                          <Building2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowEditStaff(staff)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowResetPassword(staff)}>
                          <Key className="w-4 h-4" />
                        </Button>
                        {staff.role !== 'super_agent' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveStaff(staff.agent_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredStaff.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No staff members found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Agent-to-Building Assignments</CardTitle>
                <CardDescription>Visual mapping of which agents manage which buildings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {agentMapping?.mapping?.map((agent) => (
                    <div key={agent.agent_id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          agent.role === 'super_agent' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}>
                          {agent.agent_name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{agent.agent_name}</p>
                          <p className="text-xs text-slate-500">{agent.building_count} buildings assigned</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {agent.buildings.map((building) => (
                          <div 
                            key={building.hoa_id}
                            className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                            onClick={() => fetchBuildingAnalytics(building.hoa_id)}
                          >
                            <div className="flex items-center gap-2">
                              <Home className="w-4 h-4 text-slate-400" />
                              <span className="font-medium text-sm">{building.name}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{building.units} units</p>
                          </div>
                        ))}
                        {agent.buildings.length === 0 && (
                          <p className="text-sm text-slate-400 italic">No buildings assigned</p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Unassigned Buildings */}
                  {agentMapping?.unassigned_buildings?.length > 0 && (
                    <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <p className="font-semibold text-amber-800">Unassigned Buildings</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {agentMapping.unassigned_buildings.map((building) => (
                          <div 
                            key={building.hoa_id}
                            className="p-3 bg-white rounded-lg border border-amber-200"
                          >
                            <div className="flex items-center gap-2">
                              <Home className="w-4 h-4 text-amber-500" />
                              <span className="font-medium text-sm">{building.name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Performance Tab */}
          <TabsContent value="performance">
            {loadingPerformance ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : performanceData ? (
              <div className="space-y-6">
                {/* Company Averages */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <Target className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-900">{performanceData.company_averages?.avg_overall_score || 0}%</p>
                      <p className="text-sm text-slate-500">Avg Score</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-900">{performanceData.company_averages?.avg_resolution_hours || 0}h</p>
                      <p className="text-sm text-slate-500">Avg Resolution</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <Activity className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-900">{performanceData.company_averages?.avg_response_rate || 0}%</p>
                      <p className="text-sm text-slate-500">Response Rate</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <Star className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-900">{performanceData.company_averages?.avg_satisfaction || 0}/5</p>
                      <p className="text-sm text-slate-500">Satisfaction</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Agent Rankings Chart */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                      Agent Performance Scores
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={performanceData.agents?.slice(0, 10) || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis dataKey="agent_name" type="category" width={120} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="overall_score" name="Score" fill="#6366f1" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Top Performers & Needs Improvement */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="border-emerald-200 bg-emerald-50">
                    <CardHeader>
                      <CardTitle className="text-emerald-800 flex items-center gap-2">
                        <Award className="w-5 h-5" />
                        Top Performers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {performanceData.top_performers?.map((agent, idx) => (
                          <div key={agent.agent_id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                                idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                idx === 1 ? 'bg-slate-300 text-slate-700' : 'bg-amber-600 text-white'
                              }`}>
                                {idx + 1}
                              </span>
                              <span className="font-medium">{agent.agent_name}</span>
                            </div>
                            <span className="font-bold text-emerald-600">{agent.overall_score}%</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader>
                      <CardTitle className="text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Needs Improvement
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {performanceData.needs_improvement?.length > 0 ? (
                        <div className="space-y-2">
                          {performanceData.needs_improvement?.map((agent) => (
                            <div key={agent.agent_id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                              <span className="font-medium">{agent.agent_name}</span>
                              <span className="font-bold text-amber-600">{agent.overall_score}%</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-amber-700 text-center py-4">All agents performing above threshold!</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Click to load performance data</p>
                  <Button className="mt-4" onClick={fetchPerformanceData}>Load Performance Data</Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Activity Logs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {activityLogs.map((log, idx) => (
                    <div 
                      key={log.log_id || idx}
                      className="flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors"
                    >
                      <div className="mt-0.5">{getActionIcon(log.action)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900">
                          <span className="font-medium">{log.agent_name}</span>
                          {' '}{log.action}{' '}
                          <span className="text-slate-600">{log.resource_type}</span>
                          {log.building_name && (
                            <span className="text-slate-500"> in {log.building_name}</span>
                          )}
                        </p>
                        {log.details && (
                          <p className="text-xs text-slate-500 truncate">{log.details}</p>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                  {activityLogs.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No activity logs yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Add Staff Modal */}
      {showAddStaff && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-indigo-600" />
                  Add Staff Member
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowAddStaff(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={newStaff.name} onChange={(e) => setNewStaff(prev => ({ ...prev, name: e.target.value }))} placeholder="John Smith" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={newStaff.email} onChange={(e) => setNewStaff(prev => ({ ...prev, email: e.target.value }))} placeholder="john@company.com" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={newStaff.phone} onChange={(e) => setNewStaff(prev => ({ ...prev, phone: e.target.value }))} placeholder="+27 82 123 4567" />
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input value={newStaff.job_title} onChange={(e) => setNewStaff(prev => ({ ...prev, job_title: e.target.value }))} placeholder="Property Manager" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role</Label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-md" value={newStaff.role} onChange={(e) => setNewStaff(prev => ({ ...prev, role: e.target.value }))}>
                    <option value="agent">General Agent</option>
                    <option value="super_agent">Super Agent (Admin)</option>
                  </select>
                </div>
                <div>
                  <Label>Permission Level</Label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-md" value={newStaff.permission_level} onChange={(e) => setNewStaff(prev => ({ ...prev, permission_level: e.target.value }))}>
                    <option value="read_write">Read & Write</option>
                    <option value="read_only">Read Only</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Assign Buildings</Label>
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2">
                  {buildings.map(building => (
                    <label key={building.hoa_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newStaff.assigned_buildings.includes(building.hoa_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewStaff(prev => ({ ...prev, assigned_buildings: [...prev.assigned_buildings, building.hoa_id] }));
                          } else {
                            setNewStaff(prev => ({ ...prev, assigned_buildings: prev.assigned_buildings.filter(id => id !== building.hoa_id) }));
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">{building.name}</span>
                    </label>
                  ))}
                  {buildings.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No buildings available</p>}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddStaff(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleCreateStaff} className="flex-1 bg-indigo-600 hover:bg-indigo-700" disabled={!newStaff.name || !newStaff.email}>Create Staff Member</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Edit Staff Modal */}
      {showEditStaff && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-blue-600" />
                  Edit Staff Member
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowEditStaff(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Full Name</Label>
                <Input value={showEditStaff.name} onChange={(e) => setShowEditStaff(prev => ({ ...prev, name: e.target.value }))} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={showEditStaff.phone || ''} onChange={(e) => setShowEditStaff(prev => ({ ...prev, phone: e.target.value }))} />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input value={showEditStaff.job_title || ''} onChange={(e) => setShowEditStaff(prev => ({ ...prev, job_title: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Permission Level</Label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-md" value={showEditStaff.permission_level} onChange={(e) => setShowEditStaff(prev => ({ ...prev, permission_level: e.target.value }))}>
                    <option value="read_write">Read & Write</option>
                    <option value="read_only">Read Only</option>
                  </select>
                </div>
                <div>
                  <Label>Status</Label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-md" value={showEditStaff.status} onChange={(e) => setShowEditStaff(prev => ({ ...prev, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditStaff(null)} className="flex-1">Cancel</Button>
                <Button onClick={handleUpdateStaff} className="flex-1 bg-blue-600 hover:bg-blue-700">Save Changes</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Assign Building Modal */}
      {showAssignBuilding && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[80vh] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  Assign Building to {showAssignBuilding.name}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowAssignBuilding(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              <div className="space-y-3">
                {buildings.filter(b => !showAssignBuilding.assigned_buildings?.includes(b.hoa_id)).map(building => (
                  <div key={building.hoa_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{building.name}</p>
                      <p className="text-sm text-slate-500">{building.unit_count || 0} units</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleAssignBuilding(showAssignBuilding.agent_id, building.hoa_id, 'read_only')}>
                        <Eye className="w-4 h-4 mr-1" />Read Only
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleAssignBuilding(showAssignBuilding.agent_id, building.hoa_id, 'read_write')}>
                        <Edit className="w-4 h-4 mr-1" />Read/Write
                      </Button>
                    </div>
                  </div>
                ))}
                {buildings.filter(b => !showAssignBuilding.assigned_buildings?.includes(b.hoa_id)).length === 0 && (
                  <p className="text-center text-slate-500 py-4">All buildings are already assigned</p>
                )}
              </div>
            </CardContent>
            <div className="flex-shrink-0 p-4 border-t">
              <Button variant="outline" className="w-full" onClick={() => setShowAssignBuilding(null)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-600" />
                  Reset Password
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowResetPassword(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <p className="text-slate-700 mb-2">Are you sure you want to reset the password for <strong>{showResetPassword.name}</strong>?</p>
                <p className="text-sm text-slate-500">A new temporary password will be generated.</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowResetPassword(null)} className="flex-1">Cancel</Button>
                <Button onClick={handleResetPassword} className="flex-1 bg-amber-600 hover:bg-amber-700">Reset Password</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Ticket Drilldown Modal */}
      {showTicketDrilldown && drilldownTickets && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-indigo-600" />
                  Tickets: {drilldownTickets.status.replace('_', ' ').toUpperCase()}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowTicketDrilldown(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {drilldownTickets.tickets?.map((ticket) => (
                  <div key={ticket.ticket_id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{ticket.title}</p>
                        <p className="text-sm text-slate-500">{ticket.building_name}</p>
                      </div>
                      <Badge variant={
                        ticket.priority === 'high' ? 'destructive' :
                        ticket.priority === 'medium' ? 'default' : 'secondary'
                      }>
                        {ticket.priority}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Created: {new Date(ticket.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
                {drilldownTickets.tickets?.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No tickets found</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Building Detail Modal */}
      {showBuildingDetail && buildingAnalytics && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-5xl max-h-[85vh] overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    {buildingAnalytics.building?.name}
                  </CardTitle>
                  <CardDescription>{buildingAnalytics.building?.address} • {buildingAnalytics.building?.units} units</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowBuildingDetail(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[70vh]">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingAnalytics.avg_resolution_hours}h</p>
                    <p className="text-xs text-slate-500">Avg Resolution</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingAnalytics.outstanding_issues?.length || 0}</p>
                    <p className="text-xs text-slate-500">Outstanding</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Briefcase className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingAnalytics.contractors_used?.length || 0}</p>
                    <p className="text-xs text-slate-500">Contractors</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Target className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">
                      {buildingAnalytics.ticket_breakdown?.reduce((sum, t) => sum + t.count, 0) || 0}
                    </p>
                    <p className="text-xs text-slate-500">Total Tickets</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Charts Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Ticket Status Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Ticket Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={buildingAnalytics.ticket_breakdown?.map(t => ({ name: t.status, value: t.count })) || []}
                            cx="50%"
                            cy="50%"
                            outerRadius={60}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {buildingAnalytics.ticket_breakdown?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'][index % 6]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Category Breakdown Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Tickets by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={buildingAnalytics.category_breakdown?.slice(0, 6) || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" />
                          <YAxis dataKey="category" type="category" width={80} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ticket Breakdown List */}
                <div>
                  <h4 className="font-semibold mb-3">Ticket Status Details</h4>
                  <div className="space-y-2">
                    {buildingAnalytics.ticket_breakdown?.map((item) => (
                      <div key={item.status} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm capitalize">{item.status.replace('_', ' ')}</span>
                        <Badge>{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Contractors Used */}
                <div>
                  <h4 className="font-semibold mb-3">Top Contractors</h4>
                  <div className="space-y-2">
                    {buildingAnalytics.contractors_used?.map((contractor) => (
                      <div key={contractor.contractor_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{contractor.name}</p>
                          <p className="text-xs text-slate-500">{contractor.category}</p>
                        </div>
                        <Badge variant="outline">{contractor.job_count} jobs</Badge>
                      </div>
                    ))}
                    {buildingAnalytics.contractors_used?.length === 0 && (
                      <p className="text-sm text-slate-400">No contractors assigned yet</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Outstanding Issues */}
              {buildingAnalytics.outstanding_issues?.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Outstanding Issues</h4>
                  <div className="space-y-2">
                    {buildingAnalytics.outstanding_issues?.map((issue) => (
                      <div key={issue.ticket_id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start justify-between">
                          <p className="font-medium text-sm">{issue.title}</p>
                          <Badge variant={issue.priority === 'high' ? 'destructive' : 'default'}>{issue.priority}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Since: {new Date(issue.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
